export const SURFACE_TYPE = ["Asphalt", "Schotter", "Offroad / Waldwege", "Sand oder Kies"];
export const RUNNING_SURFACE = ["Glatte Lauffläche", "Profil"];
export const BIKE_TYPE = ["Rennrad", "Gravel", "MTB"];

/**
 * factor for wind resistance
 * @param bikeType field 5a
 * @param trainingSystem field 8
 * @param trainingSessionDistance field 6
 * @returns
 */
const CW = (bikeType: string, trainingSystem: number, trainingSessionDistance: number): number => {
  const field5a = bikeType;
  const field8 = trainingSystem;
  const field6 = trainingSessionDistance;

  if (field5a == "MTB") {
    return 1;
  }

  if (field5a == "Gravel") {
    return 0.9;
  }

  if (field5a == "Rennrad") {
    return 0.7 + field8 / 1000 / field6;
  }
};

/**
 * @param runningSurface field 5b field 5b
 * @returns
 */

// field 5b form RUNNING_SURFACE
const X = (runningSurface: string): number => {
  if (runningSurface === "Glatte Lauffläche") {
    return 1;
  }
  if (runningSurface === "Profil") {
    return 1.26;
  }
};

/**
 *
 * @param surfaceType 5c
 * @param runningSurface field 5b
 * @returns
 */
const C = (surfaceType: string, runningSurface: string): number => {
  const field5c = surfaceType;
  const field5b = runningSurface;
  const XValue = X(field5b);

  if (field5c === "Asphalt") {
    return 0.003 * XValue;
  }
  if (field5c === "Schotter") {
    return 0.006 * XValue;
  }
  if (field5c === "Offroad / Waldwege") {
    return 0.02 * XValue;
  }
  if (field5c === "Sand oder Kies") {
    return 0.03 * XValue;
  }
};

/**
 *
 * @param bodyWeight field 1b
 * @param bikeWeight field 3
 * @param luggageAccessoriesWeigh field 4
 * @returns
 */
// total mass in kg (bike, rider and luggage)
export const M = (bodyWeight: number, bikeWeight: number, luggageAccessoriesWeigh: number): number => {
  const field1 = bodyWeight;
  const field3 = bikeWeight;
  const field4 = luggageAccessoriesWeigh;
  return Number((field1 + field3 + field4 / 1000)?.toFixed(2));
};

/**
 *
 * @param trainingSessionDistance field 6
 * @param trainingDurationHour field 7a
 * @param trainingDurationMin field 7b
 * @returns
 */
// speed in meters per second
const V = (trainingSessionDistance: number, trainingDurationHour: number, trainingDurationMin: number): number => {
  const field6 = trainingSessionDistance;
  const field7a = trainingDurationHour;
  const field7b = trainingDurationMin;

  const a = field6 / (field7a + field7b / 60) / 3.6;

  return Number(a?.toFixed(2));
};

/**
 *
 * @param trainingSystem field 8
 * @returns
 */
// incline as decimal number
const K = (trainingSystem: number): number => trainingSystem / 100;

/**
 *
 * @param journeyAverageHeight field 9
 * @returns
 */
// air pressure
const AP = (journeyAverageHeight: number): number => {
  const field9 = journeyAverageHeight;
  return Number((101325 * (1 - (6.5 * field9) / 288000) ** 5.256)?.toFixed(2));
};

/**
 *
 * @param temperature field 10
 * @param journeyAverageHeight field 9
 * @returns
 */
// air density
const P = (temperature: number, journeyAverageHeight: number): number => {
  const ap = AP(journeyAverageHeight);
  const field10 = temperature;
  return ap / (287.058 * (field10 + 273.15));
};

/**
 *
 * @param howTall field 2
 * @returns
 */
// body area exposed to frontal wind
const A = (howTall: number): number => {
  const field2 = howTall;
  return Number(((field2 / 100) * 0.26)?.toFixed(2));
};

/**
 *
 * @param howTall field 2
 * @param temperature field 10
 * @param trainingSystem field 8 field 8
 * @param trainingDurationHour field 7a
 * @param trainingDurationMin field 7b field 7b
 * @param trainingSessionDistance field 6
 * @returns
 */
// Performance for overcoming air resistance
export const L = (
  howTall: number,
  temperature: number,
  trainingSystem: number,
  trainingDurationHour: number,
  trainingDurationMin: number,
  trainingSessionDistance: number,
  bikeType: string,
  journeyAverageHeight
): number => {
  const cw = CW(bikeType, trainingSystem, trainingSessionDistance);
  const a = Number(A(howTall)?.toFixed(2));
  const p = Number(P(temperature, journeyAverageHeight)?.toFixed(2));
  const v = Number(V(trainingSessionDistance, trainingDurationHour, trainingDurationMin)?.toFixed(2));
  return (cw * a * p * Math.pow(v, 3)) / 2;
};

/**
 *
 * @param bodyWeight
 * @param bikeWeight field 3
 * @param luggageAccessoriesWeigh field 4
 * @param runningSurface field 5b
 * @param surfaceType 5c
 * @param trainingSystem field 8
 * @param trainingDurationHour field 7a
 * @param trainingDurationMin field 7b
 * @returns
 */
// Performance for overcoming rolling resistance
export const R = (
  bodyWeight: number,
  bikeWeight: number,
  luggageAccessoriesWeigh: number,
  runningSurface: string,
  surfaceType: string,
  trainingSessionDistance: number,
  trainingDurationHour: number,
  trainingDurationMin: number
): number => {
  const m = M(bodyWeight, bikeWeight, luggageAccessoriesWeigh);
  const c = C(surfaceType, runningSurface);
  const v = Number(V(trainingSessionDistance, trainingDurationHour, trainingDurationMin)?.toFixed(2));
  return Number((m * 9.81 * c * v)?.toFixed(2));
};

/**
 *
 * @param bodyWeight field 1b
 * @param bikeWeight field 3
 * @param luggageAccessoriesWeigh field 4
 * @param trainingSystem field 8
 * @param trainingDurationHour field 7a
 * @param trainingDurationMin field 7b
 * @param trainingSessionDistance field 6
 * @returns
 */
// Incline performance
export const S = (
  bodyWeight: number,
  bikeWeight: number,
  luggageAccessoriesWeigh: number,
  trainingSystem: number,
  trainingDurationHour: number,
  trainingDurationMin: number
): number => {
  const m = M(bodyWeight, bikeWeight, luggageAccessoriesWeigh);
  const field8 = trainingSystem;
  const field7a = trainingDurationHour;
  const field7b = trainingDurationMin;
  return (m * 9.81 * field8) / (field7a * 3600 + field7b * 60);
};

/**
 *
 * @param howTall
 * @param temperature
 * @param trainingSystem field 8
 * @param runningSurface field 5b
 * @param bodyWeight
 * @param bikeWeight field 3
 * @param luggageAccessoriesWeigh field 4
 * @param surfaceType 5c
 * @param trainingDurationHour field 7a
 * @param trainingDurationMin field 7b
 * @param trainingSessionDistance field 6
 * @returns
 */
// Overall performance
export const G = (
  howTall: number,
  temperature: number,
  trainingSystem: number,
  runningSurface: string,
  bodyWeight: number,
  bikeWeight: number,
  luggageAccessoriesWeigh: number,
  surfaceType: string,
  trainingDurationHour: number,
  trainingDurationMin: number,
  trainingSessionDistance: number,
  bikeType: string,
  journeyAverageHeight: number
): number => {
  const LValue = Number(
    L(
      howTall,
      temperature,
      trainingSystem,
      trainingDurationHour,
      trainingDurationMin,
      trainingSessionDistance,
      bikeType,
      journeyAverageHeight
    )?.toFixed(2)
  );
  const RValue = Number(
    R(
      bodyWeight,
      bikeWeight,
      luggageAccessoriesWeigh,
      runningSurface,
      surfaceType,
      trainingSessionDistance,
      trainingDurationHour,
      trainingDurationMin
    )?.toFixed(2)
  );
  const SValue = Number(
    S(
      bodyWeight,
      bikeWeight,
      luggageAccessoriesWeigh,
      trainingSystem,
      trainingDurationHour,
      trainingDurationMin
    ).toFixed(2)
  );
  return LValue + RValue + SValue;
};

/**
 *
 * @param howTall
 * @param temperature
 * @param trainingSystem field 8
 * @param runningSurface field 5b
 * @param bodyWeight
 * @param bikeWeight field 3
 * @param luggageAccessoriesWeigh field 4
 * @param surfaceType 5c
 * @param trainingDurationHour field 7a
 * @param trainingDurationMin field 7b
 * @param trainingSessionDistance field 6
 * @returns
 */
// Weight related performance
export const Gx = (
  howTall: number,
  temperature: number,
  trainingSystem: number,
  runningSurface: string,
  bodyWeight: number,
  bikeWeight: number,
  luggageAccessoriesWeigh: number,
  surfaceType: string,
  trainingDurationHour: number,
  trainingDurationMin: number,
  trainingSessionDistance: number,
  bikeType: string,
  journeyAverageHeight: number
): number => {
  const g = G(
    howTall,
    temperature,
    trainingSystem,
    runningSurface,
    bodyWeight,
    bikeWeight,
    luggageAccessoriesWeigh,
    surfaceType,
    trainingDurationHour,
    trainingDurationMin,
    trainingSessionDistance,
    bikeType,
    journeyAverageHeight
  );
  return g / bodyWeight;
};

/**
 *
 * @param howTall
 * @param temperature
 * @param trainingSystem field 8
 * @param runningSurface field 5b
 * @param bodyWeight
 * @param bikeWeight field 3
 * @param luggageAccessoriesWeigh field 4
 * @param surfaceType 5c
 * @param trainingDurationHour field 7a
 * @param trainingDurationMin field 7b
 * @param trainingSessionDistance field 6
 * @returns
 */
// Calorie consumption
export const kcal = (
  howTall: number,
  temperature: number,
  trainingSystem: number,
  runningSurface: string,
  bodyWeight: number,
  bikeWeight: number,
  luggageAccessoriesWeigh: number,
  surfaceType: string,
  trainingDurationHour: number,
  trainingDurationMin: number,
  trainingSessionDistance: number,
  bikeType: string,
  journeyAverageHeight: number
) => {
  const field7a = trainingDurationHour;
  const field7b = trainingDurationMin;
  const GValue = G(
    howTall,
    temperature,
    trainingSystem,
    runningSurface,
    bodyWeight,
    bikeWeight,
    luggageAccessoriesWeigh,
    surfaceType,
    trainingDurationHour,
    trainingDurationMin,
    trainingSessionDistance,
    bikeType,
    journeyAverageHeight
  );
  return (GValue * 0.8598 * (field7a + field7b / 60)) / 0.22;
};

/**
 * ø Steigung beim Anstieg (øS)
 * ø ascent (øS)
 * @param trainingSystem field 8
 * @param trainingSessionDistance field 6
 * @returns
 */
export const IoS = (trainingSystem: number, trainingSessionDistance: number): any => {
  const field8 = trainingSystem;
  const field6 = trainingSessionDistance * 1000;
  return (field8 * 100) / field6;
};
